import { useCallback, useContext, useState } from 'react';
import { AvailableResourceActions, can, DEVICE_CATEGORIES, DeviceState, DeviceStateCategory } from '@marsinet/commons';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';

/** Prepara sugerencias de estado para dispositivos y expone las propiedades del modal. */
export const useSuggestDeviceState = (categories: DeviceStateCategory | DeviceStateCategory[], onSaved?: () => void) => {
    const { role, admin } = useContext(MarsinetContext);
    const [target, setTarget] = useState<{ id: string, state?: DeviceState } | null>(null);

    // Une los estados de todas las categorías recibidas
    const allowedStates = ([categories].flat() as DeviceStateCategory[]).flatMap(
        (category) => DEVICE_CATEGORIES[category]
    );

    const canEditDeviceState =
        !!role && can(role, AvailableResourceActions.devices.update_state, admin);

    // Busca el dispositivo por ID y abre la sugerencia si su estado no está permitido.
    const suggestForDevice = useCallback(
        async (deviceId?: string) => {
            if (!deviceId || !canEditDeviceState) return;
            try {
                const { data } = await apiServices.devicesApi.devicesControllerFindOne(deviceId);
                const state = data.device_state as DeviceState;
                // Si ya está en alguna de las categorías permitidas, no se sugiere.
                if (allowedStates.includes(state)) return;
                setTarget({
                    id: deviceId,
                    state,
                });
            } catch {
                // sin sugerencia
            }
        },
        [canEditDeviceState, allowedStates]
    );

    // Busca el dispositivo por número de serie y sugiere un estado si hay una única coincidencia.
    const suggestForSerial = useCallback(
        async (serial?: string | number) => {
            if (serial === undefined || serial === null || serial === '') return;
            if (!canEditDeviceState) return;
            try {
                const { data } = await apiServices.devicesApi.devicesControllerFindBySerial(
                    String(serial)
                );
                if (data.length !== 1) return;
                const device = data[0];
                if (!device.d_id) return;

                const state = device.device_state as DeviceState;
                if (allowedStates.includes(state)) return;

                setTarget({
                    id: device.d_id,
                    state,
                });
            } catch {
                // sin sugerencia 
            }
        },
        [canEditDeviceState, allowedStates]
    );

    const modalProps = {
        visible: !!target,
        onHide: () => setTarget(null),
        deviceId: target?.id ?? '',
        currentState: target?.state,
        allowedStates,
        onSaved,
    };
    return { suggestForDevice, suggestForSerial, modalProps };
};